import { ArrowUpRight, ArrowRight, Check, Plus, Minus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { SiteNavbar } from "../../Navbar/SiteNavbar"
import { FooterSection } from "../../Footer/FooterSection"
import "./AboutPage.css"

const IMAGES = {
  reflectiveSigns: "/images/about/reflective-signs-riyadh.jpeg",
  solarCatEyes: "/images/about/solar-cat-eyes.png",
  crashCushions: "/images/about/crash-cushions-road-installation.jpeg",
  stepConsultation: "/images/about/step-consultation.png",
  stepSupply: "/images/about/step-supply.png",
  stepInstall: "/images/about/step-install-road-sign.jpeg",
  stepInspect: "/images/about/step-inspect-reflective-sign.jpeg",
  roadNetwork: "/images/about/road-network.png",
}

const stats = [
  { value: "2018", label: "Established in the Kingdom", tone: "brand" },
  { value: "Exclusive", label: "Agent within the Kingdom", tone: "plain" },
  { value: "5+", label: "Global manufacturers represented", tone: "accent" },
]

const features = [
  {
    title: "Reflective Sheeting & Signs",
    image: IMAGES.reflectiveSigns,
    points: [
      "Avery Dennison (USA) reflective sheeting for traffic and guide signs",
      "Latest high-speed printing machines with a full range of inks",
      "Reflective films for truck and trailer barriers",
    ],
  },
  {
    title: "Solar Road Markers",
    image: IMAGES.solarCatEyes,
    points: [
      "High-quality solar-powered cat's eyes in a range of sizes",
      "Solar-powered Sun Flower markers (Taiwanese-made)",
      "American-made Rayolite reflective plastic road markers",
    ],
  },
  {
    title: "Crash Protection & Delineation",
    image: IMAGES.crashCushions,
    points: [
      "Crash cushions for hazardous road corners and critical locations",
      "Scorpion-type cushions mounted behind trucks on highways",
      "Flexible delineator posts, traffic drums and cones of every size",
    ],
  },
]

const steps = [
  { badge: "Step 1", title: "Consultation", image: IMAGES.stepConsultation, text: "We review your project requirements and recommend the right road-safety products." },
  { badge: "Step 2", title: "Supply", image: IMAGES.stepSupply, text: "We supply certified equipment sourced directly from the world's leading manufacturers." },
  { badge: "Step 3", title: "Installation", image: IMAGES.stepInstall, text: "Our team installs signs, markers and crash cushions to specification on site." },
  { badge: "Step 4", title: "Inspection", image: IMAGES.stepInspect, text: "We verify reflectivity and quality using Delta of Denmark measuring devices." },
]

const clients = [
  "Projects for the Ministry of Transport and the Roads General Authority",
  "Projects for municipalities and regional secretariats across the Kingdom",
]

const faqs = [
  {
    q: "Are your products internationally certified?",
    a: "Yes. We supply products from globally recognized manufacturers such as Avery Dennison (USA) and Delta (Denmark), meeting international road-safety standards.",
  },
  {
    q: "What types of projects do you undertake?",
    a: "We undertake projects for the Ministry of Transport and the Roads General Authority, as well as municipalities and regional secretariats across the Kingdom.",
  },
  {
    q: "Can you handle both supply and installation?",
    a: "Yes. We supply the equipment and our team can install signs, markers, delineators and crash cushions on site to specification.",
  },
  {
    q: "How do you verify quality after installation?",
    a: "We use Delta of Denmark reflectivity measuring devices to check paint and sheeting reflectivity as well as paint-film thickness.",
  },
]

function Eyebrow({ children, variant }) {
  const { t } = useTranslation()
  return <span className={`about-eyebrow ${variant ? `about-eyebrow--${variant}` : ""}`}>{t(children)}</span>
}

function FaqItem({ q, a, defaultOpen }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div className={`about-faq__item ${open ? "is-open" : ""}`}>
      <button type="button" className="about-faq__q" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="about-faq__icon">{open ? <Minus size={15} /> : <Plus size={15} />}</span>
        <span>{t(q)}</span>
      </button>
      {open && <p className="about-faq__a">{t(a)}</p>}
    </div>
  )
}

export function AboutPage() {
  const { t } = useTranslation()
  return (
    <>
      <SiteNavbar />
      <main className="about">
        {/* Hero */}
        <section className="about-hero">
          <div className="container about-hero__grid">
            <div className="about-hero__inner">
              <Eyebrow variant="light">About Us</Eyebrow>
              <h1 className="about-hero__title">Road&apos;s Reflectors Trading Est.</h1>
              <p className="about-hero__tagline">
                {t("Founded in 2018, we supply road safety tools and equipment across the Kingdom of Saudi Arabia — and serve as the exclusive Kingdom agent for the world's leading road-safety manufacturers.")}
              </p>
              <a href="/contact" className="btn-pill btn-pill--brand about-hero__cta">
                {t("Send Us a Message")}
                <span className="btn-pill__arrow"><ArrowUpRight size={14} /></span>
              </a>
            </div>
            <div className="about-hero__media">
              <img src={IMAGES.roadNetwork} alt="Highway interchange in Saudi Arabia" />
            </div>
          </div>
        </section>

        {/* Colored stat cards */}
        <section className="about-section about-section--tight">
          <div className="container about-section__inner">
            <div className="about-stats">
              {stats.map((s) => (
                <div key={s.label} className={`about-stat about-stat--${s.tone}`}>
                  <span className="about-stat__value">{s.value}</span>
                  <span className="about-stat__label">{t(s.label)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Overview — two column editorial */}
        <section className="about-section">
          <div className="container about-section__inner about-overview">
            <div className="about-overview__head">
              <Eyebrow>Who We Are</Eyebrow>
              <h2 className="about-section__title">{t("A trusted road-safety supplier in the Kingdom")}</h2>
            </div>
            <div className="about-overview__body">
              <p>
                {t("Road's Reflectors Trading Est. was founded in 2018 to supply road safety tools and equipment — Avery reflective sheeting, solar-powered cat's eyes, ceramic road markers, Sun Flower markers, flexible posts, and crash cushions installed at road corners and mounted behind trucks and trailers.")}
              </p>
              <p>
                {t("We are the exclusive agent within the Kingdom for a group of the world's leading companies and factories that manufacture road safety tools and equipment.")}
              </p>
              <p>
                {t("Today we are one of the most active and innovative road-safety suppliers in Saudi Arabia, delivering projects for the Ministry of Transport, municipalities, the Ministry of Defense and Interior, and international, regional and local airports.")}
              </p>
            </div>
          </div>
        </section>

        {/* Alternating feature cards with "More info" */}
        <section className="about-section about-section--muted">
          <div className="container about-section__inner">
            <div className="about-center-head">
              <Eyebrow>What We Supply</Eyebrow>
              <h2 className="about-section__title">{t("Our product lines")}</h2>
            </div>
            <div className="about-features">
              {features.map((f, i) => (
                <article key={f.title} className={`about-feature ${i % 2 === 1 ? "about-feature--reverse" : ""}`}>
                  <div className="about-feature__media">
                    <img src={f.image || "/placeholder.svg"} alt={t(f.title)} />
                  </div>
                  <div className="about-feature__body">
                    <h3 className="about-feature__title">{t(f.title)}</h3>
                    <ul className="about-list">
                      {f.points.map((p, j) => (
                        <li key={j}>
                          <span className="about-list__icon"><Check size={14} /></span>
                          <span>{t(p)}</span>
                        </li>
                      ))}
                    </ul>
                    <a href="/contact" className="about-more">
                      {t("More info")} <ArrowRight size={14} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Dark process block with image grid + badges */}
        <section className="about-section">
          <div className="container">
            <div className="about-process">
              <div className="about-process__head">
                <Eyebrow variant="light">How We Work</Eyebrow>
                <h2 className="about-process__title">{t("From consultation to installation on the road")}</h2>
              </div>
              <div className="about-process__grid">
                {steps.map((s) => (
                  <div key={s.title} className="about-step">
                    <div className="about-step__media">
                      <img src={s.image || IMAGES.placeholder} alt={t(s.title)} />
                    </div>
                    <span className="about-step__badge">{t(s.badge)}</span>
                    <h3 className="about-step__title">{t(s.title)}</h3>
                    <p className="about-step__text">{t(s.text)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Clients + FAQ */}
        <section className="about-section about-section--muted">
          <div className="container about-section__inner about-bottom">
            <div className="about-card about-clients">
              <Eyebrow>Who We Serve</Eyebrow>
              <h3 className="about-card__title">{t("Our clients")}</h3>
              <ul className="about-list">
                {clients.map((item, i) => (
                  <li key={i}>
                    <span className="about-list__icon"><Check size={14} /></span>
                    <span>{t(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="about-faq">
              <div className="about-faq__head">
                <Eyebrow>FAQ</Eyebrow>
                <h2 className="about-section__title">{t("Questions? Glad you asked")}</h2>
              </div>
              {faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="about-section about-section--tight">
          <div className="container">
            <div className="about-cta">
              <Eyebrow variant="light">Get in touch</Eyebrow>
              <h2>{t("Want to work with us?")}</h2>
              <p>{t("Our team will help you with pricing, lead times and installation.")}</p>
              <a href="/contact" className="btn-pill btn-pill--brand">
                {t("Send Us a Message")}
                <span className="btn-pill__arrow"><ArrowUpRight size={14} /></span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </>
  )
}